import { AxiosError } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { ReferralFormPayload } from '@/forms/ReferralForm/types';
import { QueryParams } from '@/types/query';
import type { Referral } from '@/types/referral';
import type RepositoryResponse from '@/types/repositoryResponse';
import ajax from '@/utils/ajax';
import logAjaxError from '@/utils/logAjaxError';

export const referralRepository = {
  async getReferralsForPerson({
    host,
    params
  }: {
    host: string;
    params?: QueryParams;
  }): Promise<RepositoryResponse<Referral[]>> {
    try {
      const path = `/api/dugout/referrals/`;

      const response = await ajax.get({
        url: `${host}${path}`,
        params
      });

      return { data: response.data, isError: false };
    } catch (error) {
      logAjaxError(error);
      return {
        errorMessage: `Failed to get referral data: ${(error as AxiosError).message}`,
        error: error as AxiosError,
        isError: true
      };
    }
  },

  async getReferral({
    host,
    referralId
  }: {
    host: string;
    referralId: string;
  }): Promise<RepositoryResponse<Referral>> {
    try {
      const path = `/api/dugout/referrals/${referralId}/`;

      const response = await ajax.get({ url: `${host}${path}` });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);

      return {
        errorMessage: `Failed to get referral data: ${(error as AxiosError).message}`,
        error: error as AxiosError,
        isError: true
      };
    }
  },

  async upateReferral({
    host,
    referral
  }: {
    host: string;
    referral: ReferralFormPayload;
  }): Promise<RepositoryResponse<Referral>> {
    try {
      const path = `/api/dugout/referrals/${referral.id}/`;

      const response = await ajax.put({ url: `${host}${path}`, data: referral });

      return {
        data: response.data as unknown as Referral,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);

      return {
        errorMessage: `Failed to update referral: ${(error as AxiosError).message}`,
        error: error as AxiosError,
        isError: true
      };
    }
  },

  async createReferral({
    host,
    referral
  }: {
    host: string;
    referral: ReferralFormPayload;
  }): Promise<RepositoryResponse<Referral>> {
    try {
      const path = `/api/dugout/referrals/`;

      const response = await ajax.post({ url: `${host}${path}`, data: referral });

      return {
        data: response.data as unknown as Referral,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);

      return {
        errorMessage: `Failed to create referral: ${(error as AxiosError).message}`,
        error: error as AxiosError,
        isError: true
      };
    }
  }
};

const ReferralRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async getReferralsForPerson(params?: QueryParams): Promise<RepositoryResponse<Referral[]>> {
      return referralRepository.getReferralsForPerson({ host, params });
    },
    async createReferral(referral: ReferralFormPayload): Promise<RepositoryResponse<Referral>> {
      return referralRepository.createReferral({ host, referral });
    },
    async getReferral(referralId: string): Promise<RepositoryResponse<Referral>> {
      return referralRepository.getReferral({ host, referralId });
    },
    async updateReferral(referral: ReferralFormPayload): Promise<RepositoryResponse<Referral>> {
      return referralRepository.upateReferral({ host, referral });
    }
  };
};

export default ReferralRepository;
